import { useMemo } from 'react';
import type { AsoScenarioStatus } from '@ydistri/api-sdk';
import { useStableParams } from '../../../routes/useStableParams';
import {
  useGetAsoConfigurationQuery,
  useGetAsoScopesQuery,
  useGetAsoScenarioStoreTierConfigurationsQuery,
  useGetAsoStoreTierSettingsQuery,
  useGetAsoStoreTiersQuery,
} from '../apiAso';
import { useAsoScenarioStatus } from '../hooks/useAsoScenarioStatus';
import {
  filterAsoValidationIssuesBySeverity,
  getAsoValidationIssueCounts,
  getAsoValidationIssuesByArea,
  hasAsoValidationErrors,
  validateAso,
} from './asoValidation';
import type {
  AsoValidationIssue,
  AsoValidationIssueCounts,
  AsoValidationIssueGroups,
} from './asoValidationTypes';

export interface AsoValidationHookResult {
  asoScenarioId: number | undefined;
  scenarioStatus: AsoScenarioStatus | undefined;
  /**
   * Issues are based on currently loaded data; check isLoading and isFetching before treating an empty list as valid.
   */
  issues: AsoValidationIssue[];
  issuesByArea: AsoValidationIssueGroups;
  errors: AsoValidationIssue[];
  warnings: AsoValidationIssue[];
  issueCounts: AsoValidationIssueCounts;
  hasErrors: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

export const useAsoValidation = (): AsoValidationHookResult => {
  const { asoScenarioId } = useStableParams();
  const hasAsoScenarioId = Boolean(asoScenarioId);
  const scenarioStatus = useAsoScenarioStatus();
  const { status } = scenarioStatus;

  const effectiveAsoScenarioId = asoScenarioId ?? 0;
  const skipQueries = !hasAsoScenarioId;

  const configurationQuery = useGetAsoConfigurationQuery(effectiveAsoScenarioId, {
    skip: skipQueries,
  });

  const scopesQuery = useGetAsoScopesQuery(effectiveAsoScenarioId, { skip: skipQueries });

  const storeTierSettingsQuery = useGetAsoStoreTierSettingsQuery(effectiveAsoScenarioId, {
    skip: skipQueries,
  });

  const storeTiersQuery = useGetAsoStoreTiersQuery(effectiveAsoScenarioId, { skip: skipQueries });

  const storeTierConfigurationsQuery = useGetAsoScenarioStoreTierConfigurationsQuery(
    effectiveAsoScenarioId,
    { skip: skipQueries },
  );

  const isLoading =
    hasAsoScenarioId &&
    (scenarioStatus.isLoading ||
      configurationQuery.isLoading ||
      scopesQuery.isLoading ||
      storeTierSettingsQuery.isLoading ||
      storeTiersQuery.isLoading ||
      storeTierConfigurationsQuery.isLoading);

  const isFetching =
    hasAsoScenarioId &&
    (scenarioStatus.isFetching ||
      configurationQuery.isFetching ||
      scopesQuery.isFetching ||
      storeTierSettingsQuery.isFetching ||
      storeTiersQuery.isFetching ||
      storeTierConfigurationsQuery.isFetching);

  const isError =
    hasAsoScenarioId &&
    (scenarioStatus.isError ||
      configurationQuery.isError ||
      scopesQuery.isError ||
      storeTierSettingsQuery.isError ||
      storeTiersQuery.isError ||
      storeTierConfigurationsQuery.isError);

  const issues = useMemo(
    () =>
      validateAso({
        configuration: configurationQuery.data,
        scopes: scopesQuery.data,
        storeTierSettings: storeTierSettingsQuery.data,
        storeTiers: storeTiersQuery.data,
        storeTierConfigurations: storeTierConfigurationsQuery.data,
        // No dedicated store-tier stale signal exists yet;
        isStoreTierDataStale: false,
        scenarioStatus: status,
      }),
    [
      configurationQuery.data,
      scopesQuery.data,
      status,
      storeTierConfigurationsQuery.data,
      storeTierSettingsQuery.data,
      storeTiersQuery.data,
    ],
  );

  const errors = useMemo(() => filterAsoValidationIssuesBySeverity(issues, 'error'), [issues]);

  const warnings = useMemo(() => filterAsoValidationIssuesBySeverity(issues, 'warning'), [issues]);

  const issuesByArea = useMemo(() => getAsoValidationIssuesByArea(issues), [issues]);

  const issueCounts = useMemo(() => getAsoValidationIssueCounts(issues), [issues]);

  const hasErrors = hasAsoValidationErrors(issues);

  return useMemo(
    () => ({
      asoScenarioId,
      scenarioStatus: status,
      issues,
      issuesByArea,
      errors,
      warnings,
      issueCounts,
      hasErrors,
      isLoading,
      isFetching,
      isError,
    }),
    [
      asoScenarioId,
      errors,
      hasErrors,
      isError,
      isFetching,
      isLoading,
      issueCounts,
      issues,
      issuesByArea,
      status,
      warnings,
    ],
  );
};
